import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Sync from '@mui/icons-material/Sync';
import { buttonBaseClasses, buttonClasses, SvgIconProps } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import React, { ComponentType } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

interface ActionsMenuTriggerProps {
  isBusy?: boolean;
  onClick?: React.MouseEventHandler;
  style?: React.CSSProperties;
}

export function ActionsMenuTrigger({
  isBusy = false,
  onClick,
  style,
}: ActionsMenuTriggerProps) {
  return (
    <SmallButton
      aria-label={t('common.more')}
      onClick={onClick}
      style={style}
      color="brandV2TreadGray3"
      variant="text"
      disabled={isBusy}
      startIcon={
        isBusy ? (
          <Sync
            sx={{
              '@keyframes actions-menu-spinner': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(-360deg)' },
              },
              animation: 'actions-menu-spinner 2s linear infinite',
            }}
          />
        ) : (
          <MoreHoriz />
        )
      }
      sx={(theme) => ({
        [`&.${buttonBaseClasses.root}`]: {
          border: 'solid 1px transparent',
          borderRadius: '50%',
          height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
          minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
          p: 0,
          width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
          '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
          [`.${buttonClasses.startIcon}`]: { mx: 0 },
        },
      })}
    />
  );
}

interface ActionsMenuItemProps {
  dataUi?: string;
  disabledReason?: string;
  icon: ComponentType<SvgIconProps>;
  iconColor?: 'treadBlack' | 'treadBlue';
  label: string;
  onClick: (event: React.MouseEvent) => void;
}

export function ActionsMenuItem({
  dataUi,
  disabledReason,
  icon: Icon,
  iconColor = 'treadBlue',
  label,
  onClick,
}: ActionsMenuItemProps) {
  const menuItem = (
    <MenuItem data-ui={dataUi} disabled={Boolean(disabledReason)} onClick={onClick}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, width: '100%' }}>
        <Icon
          sx={(theme) => ({
            color: theme.brandV2.colors[iconColor],
            fontSize: '16px',
          })}
        />
        <Typography
          sx={(theme) => ({ color: theme.brandV2.colors.treadBlack, fontSize: '12px' })}
        >
          {label}
        </Typography>
      </Box>
    </MenuItem>
  );

  if (!disabledReason) {
    return menuItem;
  }

  return (
    <Tooltip placement="left" title={disabledReason}>
      <Box component="span" sx={{ display: 'block' }}>
        {menuItem}
      </Box>
    </Tooltip>
  );
}
